import AsyncStorage from '@react-native-async-storage/async-storage';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { getPrefixedStorageName } from '../../utils/storagePrefix';
import { StoreConfigResponse } from '../../services/config.service';

// Cached theme settings to prevent flash on page refresh
// These are persisted and used for initial render before fresh config loads
export interface CachedTheme {
	primaryColor: string;
	themeMode: 'light' | 'dark' | 'dynamic';
	primaryFont?: string | null;
	secondaryFont?: string | null;
	logoFont?: string | null;
	appLogo?: string | null;
	companyName?: string | null;
	appHeaderName?: string | null;
	// Splash screen
	splashLogo?: string | null;
	splashTintColor?: string | null;
}

type State = {
	darkMode: boolean;
	showOverlay: boolean;
	storeConfig: StoreConfigResponse | null;
	cachedTheme: CachedTheme | null;
	isConfigLoading: boolean;
	isConfigHydrated: boolean;
	isAppReady: boolean;
	cachedHeaderImage: {
		url: string;
		dataUrl: string;
		timestamp: number;
	} | null;
	cachedSplashLogo: {
		url: string;
		dataUrl: string;
		timestamp: number;
	} | null;
};

type Action = {
	toggleDarkMode: () => void;
	setDarkMode: (darkMode: boolean) => void;
	setShowOverlay: (showOverlay: boolean) => void;
	setStoreConfig: (config: StoreConfigResponse) => void;
	setConfigLoading: (isLoading: boolean) => void;
	clearStoreConfig: () => void;
	setConfigHydrated: (isHydrated: boolean) => void;
	setAppReady: (isReady: boolean) => void;
	setCachedHeaderImage: (url: string, dataUrl: string) => void;
	clearCachedHeaderImage: () => void;
	setCachedSplashLogo: (url: string, dataUrl: string) => void;
	clearCachedSplashLogo: () => void;
};

export const appConfigStore = create(
	persist<State & Action>(
		(set) => ({
			darkMode: false,
			showOverlay: false,
			storeConfig: null,
			cachedTheme: null,
			isConfigLoading: true,
			isConfigHydrated: false,
			isAppReady: false,
			cachedHeaderImage: null,
			cachedSplashLogo: null,
			toggleDarkMode: () => set((state) => ({ darkMode: !state.darkMode })),
			setDarkMode: (darkMode) => set({ darkMode }),
			setShowOverlay: (showOverlay) => set(() => ({ showOverlay })),
			setStoreConfig: (config) => set({ 
				storeConfig: config, 
				isConfigLoading: false,
				// Also cache theme settings for next page load
				cachedTheme: {
					primaryColor: config.config.primaryColor,
					themeMode: config.config.themeMode,
					primaryFont: config.config.primaryFont,
					secondaryFont: config.config.secondaryFont,
					logoFont: config.config.logoFont,
					appLogo: config.config.appLogo,
					companyName: config.config.companyName,
					appHeaderName: config.config.appHeaderName,
					// Splash screen
					splashLogo: config.config.splashLogo,
					splashTintColor: config.config.splashTintColor,
				}
			}),
			setConfigLoading: (isLoading) => set({ isConfigLoading: isLoading }),
			clearStoreConfig: () => set({ storeConfig: null }),
			setConfigHydrated: (isHydrated) => set({ isConfigHydrated: isHydrated }),
			setAppReady: (isReady) => set({ isAppReady: isReady }),
			setCachedHeaderImage: (url, dataUrl) => set({ 
				cachedHeaderImage: { url, dataUrl, timestamp: Date.now() } 
			}),
			clearCachedHeaderImage: () => set({ cachedHeaderImage: null }),
			setCachedSplashLogo: (url, dataUrl) => set({ 
				cachedSplashLogo: { url, dataUrl, timestamp: Date.now() } 
			}),
			clearCachedSplashLogo: () => set({ cachedSplashLogo: null }),
		}),
		{
			name: getPrefixedStorageName('app-config-storage'),
			storage: createJSONStorage(() => AsyncStorage),
			// Persist user preferences AND cached theme for smooth initial render
			// Full storeConfig is still fetched fresh to ensure latest settings
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			partialize: (state) => ({
				darkMode: state.darkMode,
				cachedTheme: state.cachedTheme,
				cachedSplashLogo: state.cachedSplashLogo, // Persist for instant logo display
				// Don't persist: storeConfig (fetched fresh), cachedHeaderImage (too large), loading states
			}) as any,
			onRehydrateStorage: () => (state) => {
				// Mark as hydrated when rehydration completes
				state?.setConfigHydrated(true);
				// Apply theme mode from cached theme if available
				const themeMode = state?.cachedTheme?.themeMode;
				if (themeMode) {
					if (themeMode === 'dark') {
						state.setDarkMode(true);
					} else if (themeMode === 'light') {
						state.setDarkMode(false);
					}
					// 'dynamic': darkMode already has the persisted user preference
				}
			},
		}
	)
);
